/* Lamp Alley shared behaviour: hanging tags sway on scroll, quick add from any tag, rail dots.
   Loaded as a module on every page. Heavy motion is skipped for reduced motion,
   weak devices and in-app browsers (html.la-lite). */

const d = document;
const root = d.documentElement;
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const lite = () => root.classList.contains('la-lite');

/* ---------- quick add ---------- */
async function addToCart(variantId, quantity, source) {
  /* 1. factory-kit: same CartLinesUpdateEvent path as Horizon's product form, so the drawer,
        bubble and fly-to-cart react natively. Loaded on first use from the URL the blocks use. */
  const kitUrl = window.LampAlley && window.LampAlley.kitUrl;
  if (kitUrl) {
    try {
      const kit = await import(kitUrl);
      if (kit && kit.addToCart) {
        const res = await kit.addToCart([{ id: Number(variantId), quantity }], source || d, 'product');
        if (res && res.ok === false) throw new Error(res.message || 'add failed');
        return;
      }
    } catch (err) {
      if (err && err.message && !/import|module|fetch/i.test(err.message)) throw err;
    }
  }
  /* 2. Shopify standard actions, 3. plain Ajax API */
  const actions = window.Shopify && window.Shopify.actions;
  if (actions && actions.updateCart) {
    const res = await actions.updateCart(
      { lines: [{ merchandiseId: String(variantId), quantity }] },
      { event: { context: 'product' } }
    );
    if (res && res.userErrors && res.userErrors.length) throw new Error(res.userErrors[0].message);
    if (actions.openCart) {
      const drawer = d.querySelector('theme-drawer#cart-drawer');
      if (!drawer || !drawer.isOpen) actions.openCart();
    }
    return;
  }
  const r = await fetch((window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] })
  });
  if (!r.ok) throw new Error('add failed');
  window.location.href = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
}

d.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-la-add]');
  if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
  e.preventDefault();
  const id = btn.dataset.laAdd;
  const qty = Number(btn.dataset.laQty || 1);
  btn.setAttribute('aria-busy', 'true');
  try {
    await addToCart(id, qty, btn);
  } catch (err) {
    const msg = btn.dataset.laError;
    if (msg) btn.setAttribute('title', msg);
    console.warn('[lamp-alley] add to cart', err);
  } finally {
    btn.removeAttribute('aria-busy');
  }
});

/* ---------- rails: dots on touch screens ---------- */
function initRail(rail) {
  if (rail.dataset.laRailReady) return;
  rail.dataset.laRailReady = '1';
  const cards = Array.from(rail.children);
  const dots = rail.parentElement && rail.parentElement.querySelector('[data-la-dots]');
  if (!dots || cards.length < 2) return;
  const label = dots.dataset.label || 'Show lamp';
  dots.innerHTML = cards
    .map((c, i) => `<button type="button" aria-label="${label} ${i + 1} / ${cards.length}" aria-current="${i === 0}"></button>`)
    .join('');
  const btns = Array.from(dots.children);
  const step = () => (cards[1] ? cards[1].offsetLeft - cards[0].offsetLeft : rail.clientWidth);
  let raf = 0;
  rail.addEventListener('scroll', () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      const i = Math.round(rail.scrollLeft / step());
      btns.forEach((b, k) => b.setAttribute('aria-current', String(k === i)));
    });
  }, { passive: true });
  btns.forEach((b, i) => b.addEventListener('click', () => rail.scrollTo({ left: i * step(), behavior: reduced() ? 'auto' : 'smooth' })));
}

/* ---------- tags sway on their strings while you scroll ---------- */
const sway = { vis: new Set(), io: null, lastY: scrollY, running: false, n: 0 };
function tick() {
  let busy = false;
  sway.vis.forEach((el) => {
    el._v += -el._a * el._f;
    el._v *= 0.9;
    el._a += el._v;
    if (Math.abs(el._a) > 0.02 || Math.abs(el._v) > 0.02) busy = true;
    else { el._a = 0; el._v = 0; }
    const a = Math.max(-7, Math.min(7, el._a)) * el._k;
    el.style.transform = a ? `rotate(${a.toFixed(2)}deg)` : '';
  });
  if (busy) requestAnimationFrame(tick);
  else sway.running = false;
}
function watchSway(scope) {
  if (reduced() || lite()) return;
  if (!sway.io) {
    sway.io = new IntersectionObserver((es) => es.forEach((e) => (e.isIntersecting ? sway.vis.add(e.target) : sway.vis.delete(e.target))), { rootMargin: '120px 0px' });
    addEventListener('scroll', () => {
      const dy = scrollY - sway.lastY;
      sway.lastY = scrollY;
      const imp = Math.max(-30, Math.min(30, dy)) * 0.03;
      sway.vis.forEach((el) => { el._v += imp * (0.8 + ((el._k * 10) % 4) / 10); });
      if (!sway.running && sway.vis.size) { sway.running = true; requestAnimationFrame(tick); }
    }, { passive: true });
  }
  scope.querySelectorAll('[data-la-sway]').forEach((el) => {
    if (el._laSway) return;
    el._laSway = 1;
    el._a = 0; el._v = 0;
    el._k = parseFloat(el.dataset.laSway) || 1;
    el._f = 0.022 + (sway.n++ % 5) * 0.004;
    sway.io.observe(el);
  });
}

function init(scope = d) {
  scope.querySelectorAll('[data-la-rail]').forEach(initRail);
  watchSway(scope);
}
init();
/* theme editor re-renders sections in place */
d.addEventListener('shopify:section:load', (e) => init(e.target));
window.LampAlley = Object.assign(window.LampAlley || {}, { init, addToCart, reduced, lite });
